// based on https://github.com/felixrieseberg/React-Dropzone-Component
import Dropzone, { DropzoneFile } from 'dropzone';
import React from 'react';
import { Icon } from './Icon';

interface IDropzoneComponentHandlers {
    init?(dropzone: Dropzone): void;
    drop?(e: DragEvent): void;
    addedfile?(file: DropzoneFile): void;
    error?(file: DropzoneFile, message: string | Error, xhr: XMLHttpRequest): void;
    uploadprogress?(file: DropzoneFile, progress: number, bytesSent: number): void;
    canceled?(file: DropzoneFile): void;
    complete?(file: DropzoneFile): void;
    queuecomplete?(): void;
}

interface IDropzoneComponentConfig {
    showFiletypeIcon?: boolean;
    iconFiletypes?: string[];
    postUrl?: string;
}

interface IDropzoneComponentProps {
    djsConfig?: Dropzone.DropzoneOptions;
    config?: IDropzoneComponentConfig;
    eventHandlers?: IDropzoneComponentHandlers;
    className?: string;
    action?: string;
    children?: React.ReactNode;
}

interface IDropzoneComponentState {
    files: Dropzone.DropzoneFile[];
}

export class DropzoneComponent extends React.Component<IDropzoneComponentProps, IDropzoneComponentState> {
    private dropzoneNode: HTMLElement | null = null;
    private dropzone!: Dropzone;
    private queueDestroy = false;

    constructor(props: IDropzoneComponentProps) {
        super(props);

        this.state = { files: [] };
    }

    /**
     * React 'componentDidMount' method
     * Sets up dropzone.js with the component.
     */
    public override componentDidMount() {
        const options = this.getDjsConfig();

        Dropzone.autoDiscover = false;

        if (!this.props.config?.postUrl && !this.props.eventHandlers?.drop) {
            console.info('Neither postUrl nor a "drop" eventHandler specified, the React-Dropzone component might misbehave.');
        }
        this.dropzone = new Dropzone(this.dropzoneNode!, options);
        this.setupEvents();
    }

    /**
     * React 'componentWillUnmount'
     * Removes dropzone.js (and all its globals) if the component is being unmounted
     */
    public override componentWillUnmount() {
        if (this.dropzone) {
            const files = this.dropzone.getActiveFiles();

            if (files.length > 0) {
                // Well, seems like we still have stuff uploading.
                // This is dirty, but let's keep trying to get rid
                // of the dropzone until we're done here.
                this.queueDestroy = true;

                const destroyInterval = window.setInterval(() => {
                    if (!this.queueDestroy) {
                        window.clearInterval(destroyInterval);
                        return;
                    }

                    if (this.dropzone.getActiveFiles().length === 0) {
                        this.dropzone = this.destroy(this.dropzone);
                        window.clearInterval(destroyInterval);
                        return;
                    }
                }, 500);
            } else {
                this.dropzone = this.destroy(this.dropzone);
            }
        }
    }

    /**
     * React 'componentDidUpdate'
     * If the Dropzone hasn't been created, create it
     */
    public override componentDidUpdate() {
        this.queueDestroy = false;

        if (!this.dropzone) {
            this.dropzone = new Dropzone(this.dropzoneNode!, this.getDjsConfig());
        }
    }

    /**
     * React 'UNSAFE_componentWillUpdate'
     * Update Dropzone options each time the component updates.
     */
    public override UNSAFE_componentWillUpdate() {
        const djsConfigObj = this.getDjsConfig();

        this.dropzone.options = { ...this.dropzone.options, ...djsConfigObj };
    }

    /**
     * React 'render'
     */
    public override render() {
        const icons: JSX.Element[] = [];
        const { files } = this.state;
        const { config } = this.props;
        const className = this.props.className ? `filepicker dropzone ${this.props.className}` : 'filepicker dropzone';

        if (config?.showFiletypeIcon && config.iconFiletypes && (!files || files.length < 1)) {
            for (let i = 0; i < config.iconFiletypes.length; i = i + 1) {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                icons.push(<Icon filetype={config.iconFiletypes[i]!} key={`icon-component${i}`} />);
            }
        }

        return (
            <>
                {!this.props.config?.postUrl && this.props.action ? (
                    <form
                        action={this.props.action}
                        className={className}
                        ref={(node) => {
                            this.dropzoneNode = node;
                        }}
                    >
                        {icons}
                        {this.props.children}
                    </form>
                ) : (
                    <div
                        className={className}
                        ref={(node) => {
                            this.dropzoneNode = node;
                        }}
                    >
                        {icons} {this.props.children}
                    </div>
                )}
            </>
        );
    }

    /**
     * Takes event handlers in this.props.eventHandlers
     * and binds them to dropzone.js events
     */
    private setupEvents() {
        const eventHandlers = this.props.eventHandlers as any;

        if (!this.dropzone || !eventHandlers) {
            return;
        }

        for (const eventHandler in eventHandlers) {
            if (Object.prototype.hasOwnProperty.call(eventHandlers, eventHandler) && eventHandlers[eventHandler]) {
                // Check if there's an array of event handlers
                if (Object.prototype.toString.call(eventHandlers[eventHandler]) === '[object Array]') {
                    for (let i = 0; i < eventHandlers[eventHandler].length; i = i + 1) {
                        // Check if it's an init handler
                        if (eventHandler === 'init') {
                            eventHandlers[eventHandler][i](this.dropzone);
                        } else {
                            this.dropzone.on(eventHandler, eventHandlers[eventHandler][i]);
                        }
                    }
                } else {
                    if (eventHandler === 'init') {
                        eventHandlers[eventHandler](this.dropzone);
                    } else {
                        this.dropzone.on(eventHandler, eventHandlers[eventHandler]);
                    }
                }
            }
        }

        this.dropzone.on('addedfile', (file) => {
            if (!file) {
                return;
            }

            window.onbeforeunload = () => 'Are you sure you want to close this page?';

            const files = this.state.files || [];

            files.push(file);
            this.setState({ files });
        });

        this.dropzone.on('removedfile', (file) => {
            if (!file) {
                return;
            }

            const files = this.state.files || [];
            files.forEach((fileInFiles, i) => {
                if (fileInFiles.name === file.name && fileInFiles.size === file.size) {
                    files.splice(i, 1);
                }
            });

            this.setState({ files });
        });

        this.dropzone.on('queuecomplete', () => {
            window.onbeforeunload = null;
        });
    }

    /**
     * Removes ALL listeners and Destroys dropzone. see https://github.com/enyo/dropzone/issues/1175
     * @param dropzone
     */
    private destroy(dropzone: Dropzone) {
        dropzone.off();
        return dropzone.destroy();
    }

    /**
     * Configuration of Dropzone.js. Defaults are
     * overriden by the 'djsConfig' property
     * For a full list of possible configurations,
     * please consult
     * http://www.dropzonejs.com/#configuration
     */
    private getDjsConfig() {
        const urlConfig = {
            url: this.props.config?.postUrl ?? (null as unknown as string), // @types/dropzone are incorrect
        };

        const options: Dropzone.DropzoneOptions = { ...urlConfig, ...(this.props.djsConfig ?? {}) };

        if (options.maxFiles === undefined) {
            options.maxFiles = null as unknown as number; // @types/dropzone are incorrect;
        }

        return options;
    }
}
